import { ExpandLess, ExpandMore, Refresh } from "@mui/icons-material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Button, ButtonGroup, PageHeader, Spacer } from "common";
import DownloadButton from "common/components/DownloadButton/DownloadButton";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import DateRangeSelector2 from "components/DateRangeSeletor/DateRangeSelector2";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import useDictionaries from "features/dictionaries/useDictionaries";
import { formatPrice } from "helpers/priceHelper";
import I18n from "i18n";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  getRebookFeeReport,
  RebookFeeReportResponse,
} from "./rebookFeeReportApi";

export default function RebookFeeReportView() {
  const { dictionaries } = useDictionaries();
  const [range, setRange] = useState({
    start: moment().startOf("month").format("YYYY-MM-DD"),
    end: moment().endOf("month").format("YYYY-MM-DD"),
  });
  const [data, setData] = useState<RebookFeeReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [currency, setCurrency] = useState<"HUF" | "EUR">("EUR");
  const [programIds, setProgramIds] = useState<number[]>([]);
  const [boatIds, setBoatIds] = useState<number[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAllPrograms, setShowAllPrograms] = useState(false);

  useEffect(() => {
    loadReport();
  }, [range.start, range.end, currency, programIds, boatIds]);

  const params = {
    from: range.start,
    to: range.end,
    currency,
    program_ids: programIds,
    boat_ids: boatIds,
  };
  const activeFilterCount = programIds.length + boatIds.length;

  async function loadReport() {
    if (!range.start || !range.end) return;

    setLoading(true);
    setError(false);
    try {
      const response = await getRebookFeeReport(params);
      if (
        response.success &&
        Array.isArray(response.data?.items) &&
        (response.data?.currency === "EUR" ||
          response.data?.currency === "HUF") &&
        typeof response.data?.total === "number"
      ) {
        setData(response.data);
      } else {
        setData(null);
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <PageHeader
        avatar={<AssessmentIcon />}
        title={I18n.t("RebookFeeReport.title")}
      >
        <ButtonGroup
          size="small"
          options={[
            { value: "EUR", label: "EUR" },
            { value: "HUF", label: "HUF" },
          ]}
          value={currency}
          onChange={(event: any) => setCurrency(event.target.value)}
        />
        <IconButton onClick={loadReport} disabled={loading}>
          <Refresh />
        </IconButton>
      </PageHeader>
      <Card sx={{ mb: 2, position: "relative" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <DateRangeSelector2
              numberOfCalendars={1}
              numberOfYears={0}
              value={{ start: moment(range.start), end: moment(range.end) }}
              onChange={(start, end) => {
                if (start && end) {
                  setRange({
                    start: start.format("YYYY-MM-DD"),
                    end: end.format("YYYY-MM-DD"),
                  });
                }
              }}
              dateButtonMinWidth={150}
            />
            <Spacer right />
            <DownloadButton
              url="/reports/rebook-fees/excel"
              params={params}
              filename={`atfoglalasi_dij_riport_${range.start}_${range.end}.xlsx`}
              disabled={loading}
            >
              {I18n.t("RebookFeeReport.export")}
            </DownloadButton>
            <Typography variant="h6" sx={{ flexShrink: 0 }}>
              {data?.currency || currency}:{" "}
              {formatPrice(
                data?.total,
                data?.currency || currency,
                false,
                (data?.currency || currency) === "HUF" ? 0 : 2,
              ) || ((data?.currency || currency) === "HUF" ? "0 Ft" : "0 EUR")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Button
              variant="text"
              size="small"
              endIcon={filtersOpen ? <ExpandLess /> : <ExpandMore />}
              title={I18n.t(
                filtersOpen
                  ? "RebookFeeReport.hideFilters"
                  : "RebookFeeReport.showFilters",
              )}
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              {I18n.t("RebookFeeReport.filters")}
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Button>
          </Box>
          <Collapse in={filtersOpen} sx={{ maxWidth: "100%" }}>
            <Box
              sx={{
                mt: 1,
                pt: 2,
                px: 1,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {I18n.t("RebookFeeReport.boats")}
              </Typography>
              <BoatCheckboxSelector
                value={boatIds}
                onChange={event =>
                  setBoatIds(
                    (event.target?.value ? event.target.value : []) as number[],
                  )
                }
              />
            </Box>
            <Box sx={{ mt: 2, px: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {I18n.t("RebookFeeReport.programs")}
              </Typography>
              <ProgramCheckboxSelector
                includeInactive
                value={programIds}
                showMore={showAllPrograms}
                setShowMore={showMore => setShowAllPrograms(!!showMore)}
                onChange={event =>
                  setProgramIds(
                    (event.target?.value ? event.target.value : []) as number[],
                  )
                }
              />
            </Box>
          </Collapse>
          {loading && (
            <LinearProgress
              sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
            />
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {I18n.t("RebookFeeReport.error")}
        </Alert>
      )}
      {!loading && !error && data?.items.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {I18n.t("RebookFeeReport.noData")}
        </Alert>
      )}
      {!!data?.items.length && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{I18n.t("RebookFeeReport.rebookedAt")}</TableCell>
                <TableCell>{I18n.t("RebookFeeReport.originalTime")}</TableCell>
                <TableCell>
                  {I18n.t("RebookFeeReport.originalProgram")}
                </TableCell>
                <TableCell align="right">
                  {I18n.t("RebookFeeReport.headcount")}
                </TableCell>
                <TableCell align="right">
                  {I18n.t("RebookFeeReport.fee")}
                </TableCell>
                <TableCell>{I18n.t("RebookFeeReport.currency")}</TableCell>
                <TableCell>{I18n.t("RebookFeeReport.bookingName")}</TableCell>
                <TableCell>{I18n.t("RebookFeeReport.partner")}</TableCell>
                <TableCell>{I18n.t("RebookFeeReport.reason")}</TableCell>
                <TableCell>{I18n.t("RebookFeeReport.coordinator")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(item => (
                <TableRow key={item.booking_id}>
                  <TableCell>
                    {moment(item.rebooked_at).format("L LT")}
                  </TableCell>
                  <TableCell>
                    {item.original_start_time
                      ? moment(item.original_start_time).format("L LT")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {item.original_program_name ? (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        {!!item.original_program_id && (
                          <Box
                            component="span"
                            aria-hidden
                            sx={{
                              width: 8,
                              height: 8,
                              flex: "0 0 8px",
                              borderRadius: "50%",
                              backgroundColor: dictionaries.programs?.find(
                                program =>
                                  program.id === item.original_program_id,
                              )?.color,
                            }}
                          />
                        )}
                        <span>{item.original_program_name}</span>
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="right">{item.number_of_guests}</TableCell>
                  <TableCell align="right">
                    {formatPrice(
                      item.rebook_price,
                      item.currency,
                      false,
                      item.currency === "EUR" ? 2 : 0,
                    )}
                  </TableCell>
                  <TableCell>{item.currency}</TableCell>
                  <TableCell>{item.booking_name || "-"}</TableCell>
                  <TableCell>{item.partner_name || "-"}</TableCell>
                  <TableCell sx={{ minWidth: 220, whiteSpace: "pre-wrap" }}>
                    {item.reason || "-"}
                  </TableCell>
                  <TableCell>{item.coordinator_name || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
